/**
 * DefencIQ Hybrid DISP Readiness & Assurance Report Builder
 *
 * Phase 1: Data layer. Builds 9 additive report sections from existing
 * assessment outputs (scores, gating, DISP gap analysis, CSQ mapping,
 * priority gaps, evidence/policy multipliers). NO LLM, fully deterministic.
 *
 * Consumers: FinalReportPanel.jsx (UI), report-generator.js (JSON export
 * via _report_metadata.hybrid_report). Phase 2 will wire pdf-generator.js.
 */

import { DOMAIN_WEIGHTS } from '@/config/domain-weights';
import { computeEvidenceReadiness } from './evidence-readiness';

// ============================================================================
// SUPER-DOMAIN MAPPING (Governance / Personnel / Physical / ICT)
// Aligned to DISP submission domains used in CSQ mapping.
// ============================================================================
const SUPER_DOMAIN_LABELS = {
  governance: 'Governance & Risk Management',
  personnel_security: 'Personnel Security',
  physical_security: 'Physical Security',
  ict_security: 'ICT Security',
};

const SUPER_DOMAIN_SECTIONS = {
  governance: ['policy_governance', 'compliance_and_governance', 'foci'],
  personnel_security: ['personnel_security'],
  physical_security: ['physical_security'],
  ict_security: [
    'identity_and_mfa', 'endpoint_management', 'application_control',
    'patch_management', 'privileged_access', 'office_macros_and_user_hardening',
    'logging_and_monitoring', 'vulnerability_management', 'backups',
  ],
};

const SUPER_DOMAIN_ORDER = ['governance', 'personnel_security', 'physical_security', 'ict_security'];

// ============================================================================
// Helpers
// ============================================================================
const weightedAvg = (sectionIds, domainScores) => {
  let totalScore = 0;
  let totalWeight = 0;
  sectionIds.forEach(id => {
    const w = DOMAIN_WEIGHTS[id] || 0;
    const s = domainScores[id]?.percentage;
    if (w > 0 && typeof s === 'number') {
      totalScore += s * w;
      totalWeight += w;
    }
  });
  return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
};

const statusFromScore = (pct, gatingFails) => {
  if (gatingFails > 0 || pct < 50) return 'Fail';
  if (pct < 75) return 'Partial';
  return 'Pass';
};

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// ============================================================================
// SECTION 1 — EXECUTIVE SNAPSHOT
// ============================================================================
const buildExecutiveSnapshot = (ctx) => {
  const {
    engagement, answers, finalScore, scoreBand, dispReadiness, csqMapping,
    gatingStatus, priorityGaps, domainFindings,
  } = ctx;

  // DISP submission status (mapped to spec values)
  const statusMap = {
    NOT_READY: 'Not Ready',
    PARTIALLY_READY: 'Conditionally Ready',
    READY_FOR_SUBMISSION: 'Ready',
  };
  const submission_status = statusMap[dispReadiness?.submission_status] || 'Not Ready';

  // Submission confidence from CSQ engine (LOW/MODERATE/HIGH)
  const submission_confidence = csqMapping?.submission_confidence
    ? cap(csqMapping.submission_confidence.toLowerCase())
    : 'Low';

  // Estimated time to readiness — derive from gating + critical gaps
  const gatingFails = gatingStatus?.score0Count || 0;
  const gatingPartials = gatingStatus?.score1Count || 0;
  const gatingNotPassed = gatingFails + gatingPartials;
  const criticalGaps = priorityGaps.filter(g => g.priority === 'critical').length;
  const highGaps = priorityGaps.filter(g => g.priority === 'high').length;
  let estimated_time_to_readiness;
  if (gatingNotPassed === 0 && criticalGaps === 0 && finalScore >= 85) estimated_time_to_readiness = '0-2 weeks';
  else if (gatingFails === 0 && gatingPartials <= 2 && criticalGaps <= 2 && finalScore >= 70) estimated_time_to_readiness = '3-5 weeks';
  else if (criticalGaps <= 5 && finalScore >= 50) estimated_time_to_readiness = '6-10 weeks';
  else if (criticalGaps <= 10) estimated_time_to_readiness = '10-16 weeks';
  else estimated_time_to_readiness = '16+ weeks';

  // Top 5 business risks from failed/weak domains
  const weakDomains = [...domainFindings]
    .filter(d => (d.score || 0) < 60)
    .sort((a, b) => a.score - b.score)
    .slice(0, 5);

  const top_business_risks = weakDomains.map(d => ({
    title: `${d.domain_name} maturity at ${d.score}%`,
    description: d.score < 30
      ? `Critical exposure across ${d.domain_name.toLowerCase()} controls; CSQ submission in this area will fail without remediation.`
      : `Insufficient ${d.domain_name.toLowerCase()} maturity will trigger Defence clarification or evidence requests.`,
  }));
  if (gatingFails > 0) {
    top_business_risks.unshift({
      title: `${gatingFails} gating control failure${gatingFails > 1 ? 's' : ''}`,
      description: 'Gating controls are not implemented. Defence will reject the CSQ until these are remediated to a demonstrable standard.',
    });
  } else if (gatingPartials > 0) {
    top_business_risks.unshift({
      title: `${gatingPartials} gating control${gatingPartials > 1 ? 's' : ''} only partially implemented`,
      description: 'Gating controls exist but lack consistent enforcement and audit-ready evidence. This is sufficient to prevent a defensible DISP submission.',
    });
  }
  // Trim to 5
  const trimmed_risks = top_business_risks.slice(0, 5);

  // Deterministic narrative — distinguishes hard fail from partial/soft block
  const clientName = engagement?.client_name || 'The organisation';
  const bandLabel = scoreBand?.label || 'Developing';
  let gatingNarrative;
  if (gatingFails > 0 && gatingPartials > 0) {
    gatingNarrative = `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed and ${gatingPartials} ${gatingPartials > 1 ? 'are' : 'is'} only partially implemented. The fully-failed controls are a hard block on DISP submission; the partial controls lack the consistent enforcement and audit-ready evidence required for a defensible submission.`;
  } else if (gatingFails > 0) {
    gatingNarrative = `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed. This is a hard block on DISP submission - Defence will reject the CSQ until each is remediated to a demonstrable standard.`;
  } else if (gatingPartials > 0) {
    gatingNarrative = `While no gating controls are fully failed, ${gatingPartials} gating control${gatingPartials > 1 ? 's are' : ' is'} only partially implemented and lack${gatingPartials > 1 ? '' : 's'} consistent enforcement and audit-ready evidence. This is sufficient to prevent a defensible DISP submission.`;
  } else {
    gatingNarrative = 'All gating controls are fully implemented, which provides a structural foundation for a defensible DISP submission.';
  }
  const narrative = [
    `${clientName} has achieved a weighted ML2 readiness score of ${finalScore}% (${bandLabel}).`,
    gatingNarrative,
    `${criticalGaps} critical and ${highGaps} high-priority gaps have been identified across the assessment.`,
    submission_confidence === 'High'
      ? 'Submission confidence is high, subject to evidence completeness and currency.'
      : submission_confidence === 'Moderate'
      ? 'Submission confidence is moderate; Defence will likely request clarification or supplementary evidence.'
      : 'Submission confidence is low; the organisation must defer submission until remediation reduces material risk.',
    `Estimated time to readiness: ${estimated_time_to_readiness}.`,
  ].join(' ');

  return {
    submission_status,
    submission_confidence,
    weighted_ml2_score: finalScore,
    score_band: bandLabel,
    estimated_time_to_readiness,
    gating_failures: gatingFails,
    gating_partials: gatingPartials,
    critical_gaps: criticalGaps,
    high_gaps: highGaps,
    top_business_risks: trimmed_risks,
    narrative,
    // Evidence Readiness (independent informational signal) - shown alongside ML2 on
    // Executive Snapshot so boards see both control readiness and evidence coverage.
    evidence_readiness_assessment: computeEvidenceReadiness(answers || {}),
  };
};

// ============================================================================
// SECTION 1.5 — COMMERCIAL IMPACT & SUBMISSION RISK
// ============================================================================
const buildCommercialImpact = (ctx, executiveSnapshot) => {
  const { evidenceMultiplier, gatingStatus, priorityGaps, finalScore } = ctx;
  const gatingFails = gatingStatus?.score0Count || 0;
  const gatingPartials = gatingStatus?.score1Count || 0;
  const highGaps = priorityGaps.filter(g => g.priority === 'high').length;
  const criticalGaps = priorityGaps.filter(g => g.priority === 'critical').length;

  // Map time-to-readiness window into a commercial delay statement
  const delayMap = {
    '0-2 weeks': '0-2 weeks',
    '3-5 weeks': '3-5 weeks',
    '6-10 weeks': '6-10 weeks',
    '10-16 weeks': '10-16 weeks',
    '16+ weeks': '16 weeks or more',
  };
  const estimated_contract_delay = delayMap[executiveSnapshot.estimated_time_to_readiness] || executiveSnapshot.estimated_time_to_readiness;

  // CSQ rejection likelihood
  let csq_rejection_likelihood;
  if (gatingFails > 0 || (evidenceMultiplier < 0.7 && gatingPartials >= 3)) csq_rejection_likelihood = 'High';
  else if (gatingPartials > 0 || evidenceMultiplier < 0.85 || finalScore < 75) csq_rejection_likelihood = 'Medium';
  else csq_rejection_likelihood = 'Low';

  // Resubmission risk — driven by evidence maturity
  let resubmission_risk;
  if (evidenceMultiplier < 0.7) resubmission_risk = 'High';
  else if (evidenceMultiplier < 0.85) resubmission_risk = 'Medium';
  else resubmission_risk = 'Low';

  // Delivery risk — driven by volume of high-priority work
  let delivery_risk;
  if ((criticalGaps + highGaps) > 15) delivery_risk = 'High';
  else if ((criticalGaps + highGaps) > 7) delivery_risk = 'Medium';
  else delivery_risk = 'Low';

  // Deterministic narrative connecting control gaps to commercial consequences
  const consequenceClause =
    csq_rejection_likelihood === 'High'
      ? 'will likely result in CSQ rejection and a requirement to remediate before any further engagement'
      : csq_rejection_likelihood === 'Medium'
      ? 'will likely result in CSQ clarification cycles and supplementary evidence requests from Defence'
      : 'is expected to clear CSQ validation, subject to evidence currency';

  const narrative =
    `At the current maturity level, proceeding with DISP submission ${consequenceClause}, ` +
    `leading to an estimated delay of ${estimated_contract_delay} in Defence contract eligibility. ` +
    (csq_rejection_likelihood === 'Low'
      ? 'Commercial exposure is limited; the organisation can pursue contract conversations in parallel with submission packaging.'
      : `Until remediation closes the ${criticalGaps + highGaps} highest-priority gaps and centralises evidence, opportunities that require active DISP membership cannot be pursued without commercial risk.`);

  return {
    estimated_contract_delay,
    csq_rejection_likelihood,
    resubmission_risk,
    delivery_risk,
    narrative,
  };
};

// ============================================================================
// SECTION 2 — HOW TO READ THIS REPORT
// ============================================================================
const buildHowToRead = () => ({
  sections: [
    {
      heading: 'Essential Eight ML2',
      body: 'Essential Eight Maturity Level 2 (ML2) is the Australian Cyber Security Centre target baseline for organisations handling Defence-related data. It defines a set of preventive and detective controls (e.g. application control, MFA, patching SLAs, restricted admin privileges, daily backups) that must be both technically enforced and demonstrably operational.',
    },
    {
      heading: 'Gating Controls',
      body: 'Gating controls are individual assessment items where a failing answer caps overall readiness regardless of how strong other domains are. A single gating failure (for example, no MFA on privileged accounts) is sufficient to block a successful DISP submission.',
    },
    {
      heading: 'DISP CSQ Validation',
      body: 'The Defence Industry Security Program (DISP) Compliance Self-Questionnaire (CSQ) is the formal validation pathway used by Defence to assess membership eligibility. The CSQ requires evidenced, repeatable controls across Governance, Personnel Security, Physical Security, and ICT Security. Submissions are reviewed against documentation, configuration evidence, and audit artefacts.',
    },
    {
      heading: 'Three Maturity Layers',
      body: 'Discovery findings are organised across three independent layers. (1) Control implementation - is the technical control actually in place and operating? (2) Policy maturity - is the control governed by an approved, reviewed, and enforced policy? (3) Evidence readiness - can the organisation prove the control to a Defence assessor through audit-ready artefacts? A control can be technically present but fail submission if policy or evidence is missing.',
    },
  ],
});

// ============================================================================
// SECTION 3 — DECISION VIEW
// ============================================================================
const buildDecisionView = (ctx) => {
  const { domainScores, domainFindings, gatingStatus, priorityGaps, finalScore } = ctx;
  const gatingFails = gatingStatus?.score0Count || 0;

  // Map gating failures to super-domain
  const gatingBySection = {};
  (gatingStatus?.controls || []).forEach(c => {
    if (c.score === 0) {
      gatingBySection[c.section_id] = (gatingBySection[c.section_id] || 0) + 1;
    }
  });

  const domain_status_table = SUPER_DOMAIN_ORDER.map(superId => {
    const sectionIds = SUPER_DOMAIN_SECTIONS[superId];
    const pct = weightedAvg(sectionIds, domainScores);
    const sectionGatingFails = sectionIds.reduce((sum, sid) => sum + (gatingBySection[sid] || 0), 0);
    const status = statusFromScore(pct, sectionGatingFails);

    const sectionFindings = domainFindings.filter(d => sectionIds.includes(d.domain_id));
    const totalAnswered = sectionFindings.reduce((s, d) => s + (d.answered || 0), 0);
    const totalQuestions = sectionFindings.reduce((s, d) => s + (d.total || 0), 0);

    let narrative;
    if (status === 'Pass') {
      narrative = `${SUPER_DOMAIN_LABELS[superId]} demonstrates strong control maturity at ${pct}% with no gating failures.`;
    } else if (status === 'Partial') {
      narrative = `${SUPER_DOMAIN_LABELS[superId]} is partially mature at ${pct}%${sectionGatingFails > 0 ? ` with ${sectionGatingFails} gating failure${sectionGatingFails > 1 ? 's' : ''}` : ''}; targeted uplift required before submission.`;
    } else {
      narrative = `${SUPER_DOMAIN_LABELS[superId]} is below submission threshold at ${pct}%${sectionGatingFails > 0 ? `; ${sectionGatingFails} gating control${sectionGatingFails > 1 ? 's' : ''} blocking submission` : ''}.`;
    }

    return {
      domain_id: superId,
      domain: SUPER_DOMAIN_LABELS[superId],
      status,
      percent: pct,
      gating_failures: sectionGatingFails,
      answered: totalAnswered,
      total: totalQuestions,
      narrative,
    };
  });

  // Final recommendation — distinguishes gating fail (hard block) from partial gating (soft block)
  const gatingPartials = gatingStatus?.score1Count || 0;
  let final_recommendation;
  let final_recommendation_reason;
  if (gatingFails > 0) {
    final_recommendation = 'Do Not Submit';
    final_recommendation_reason = `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed. This is a hard block - the organisation must remediate gating controls before submission.`;
  } else if (finalScore < 70 || domain_status_table.some(d => d.status === 'Fail')) {
    final_recommendation = 'Do Not Submit';
    final_recommendation_reason = `Overall readiness at ${finalScore}% is below the threshold required for a defensible DISP submission.`;
  } else if (gatingPartials > 0) {
    final_recommendation = 'Submit with Conditions';
    final_recommendation_reason = `${gatingPartials} gating control${gatingPartials > 1 ? 's are' : ' is'} only partially implemented. Submission may proceed with explicit acknowledgement of remediation in flight; expect Defence clarification on these controls.`;
  } else if (finalScore < 85 || domain_status_table.some(d => d.status === 'Partial')) {
    final_recommendation = 'Submit with Conditions';
    final_recommendation_reason = `Submission may proceed with explicit acknowledgement of remediation in progress for ${domain_status_table.filter(d => d.status !== 'Pass').map(d => d.domain).join(', ')}. Defence will likely request clarification.`;
  } else {
    final_recommendation = 'Ready for Submission';
    final_recommendation_reason = 'All super-domains meet submission threshold and no gating controls are failing or partially implemented.';
  }

  // Required before submission: tight top-8 list of NON-NEGOTIABLE actions (gating + critical only).
  // Operational depth lives in remediation_roadmap; this list is the executive's go/no-go set.
  const blockingGaps = priorityGaps.filter(g => {
    const isGatingControl = (gatingStatus?.controls || []).some(c => c.field_id === g.field_id && c.score < 2);
    return isGatingControl || g.priority === 'critical';
  });
  const sortedGaps = [...blockingGaps].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    const pd = (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
    if (pd !== 0) return pd;
    return (b.weight || 0) - (a.weight || 0);
  });
  const required_before_submission = sortedGaps.slice(0, 8).map(g => ({
    action: g.gap?.replace(/^Is\s+/i, 'Address: ').replace(/\?$/, '') || 'Remediate identified control gap',
    priority: g.priority,
    domain: g.domain,
    field_id: g.field_id,
  }));

  return {
    domain_status_table,
    final_recommendation,
    final_recommendation_reason,
    required_before_submission,
  };
};

// ============================================================================
// SECTION 4 — PER-DOMAIN ENHANCEMENTS
// ============================================================================
const buildDomainEnhancements = (ctx) => {
  const { domainScores, gatingStatus, priorityGaps, dispGapAnalysis } = ctx;
  const gatingControls = gatingStatus?.controls || [];

  const result = {};
  SUPER_DOMAIN_ORDER.forEach(superId => {
    const sectionIds = SUPER_DOMAIN_SECTIONS[superId];
    const pct = weightedAvg(sectionIds, domainScores);
    const sectionGatingControls = gatingControls.filter(c => sectionIds.includes(c.section_id));
    const sectionGatingFails = sectionGatingControls.filter(c => c.score === 0).length;

    // Risk to DISP Submission
    const will_fail_csq = pct < 50 || sectionGatingFails > 0;
    const defence_will_clarify = (pct >= 50 && pct < 75) || sectionGatingControls.some(c => c.score === 1);
    const is_gating_failure = sectionGatingFails > 0;

    let risk_narrative;
    if (will_fail_csq && is_gating_failure) {
      risk_narrative = `This domain will fail CSQ validation. ${sectionGatingFails} gating control${sectionGatingFails > 1 ? 's are' : ' is'} not implemented and Defence will reject submission in this area.`;
    } else if (will_fail_csq) {
      risk_narrative = `Domain maturity at ${pct}% is below the threshold required to pass CSQ validation in this area.`;
    } else if (defence_will_clarify) {
      risk_narrative = 'Defence assessors are likely to request clarification or supplementary evidence in this domain prior to acceptance.';
    } else {
      risk_narrative = 'This domain is well-positioned for CSQ validation, subject to evidence completeness.';
    }

    // Control priority table — pull from priority gaps in scope, plus partial gating controls
    const sectionPriorityGaps = priorityGaps.filter(g => sectionIds.includes(g.domain));
    const control_priority_table = sectionPriorityGaps
      .sort((a, b) => {
        const order = { critical: 0, high: 1, medium: 2, low: 3 };
        const pd = (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
        if (pd !== 0) return pd;
        return (b.weight || 0) - (a.weight || 0);
      })
      .slice(0, 12)
      .map(g => ({
        control: g.gap?.replace(/\?$/, '') || g.field_id,
        status: g.score === 0 ? 'Fail' : g.score === 1 ? 'Partial' : 'Pass',
        weight: g.weight || 0,
        risk_level: g.priority === 'critical' ? 'Critical'
          : g.priority === 'high' ? 'High'
          : g.priority === 'medium' ? 'Medium' : 'Low',
        is_gating: sectionGatingControls.some(c => c.field_id === g.field_id),
      }));

    // Standardised audit-ready evidence list — pull from DISP gap analysis
    const evidenceSet = new Set();
    (dispGapAnalysis || []).forEach(domain => {
      // Match super domain by mapping
      const matches =
        (superId === 'governance' && domain.domain_id === 'governance') ||
        (superId === 'personnel_security' && domain.domain_id === 'personnel_security') ||
        (superId === 'physical_security' && domain.domain_id === 'physical_security') ||
        (superId === 'ict_security' && domain.domain_id === 'ict_cyber');
      if (!matches) return;
      domain.controls.forEach(c => (c.evidence_required || []).forEach(e => evidenceSet.add(e)));
    });
    const evidence_required = [...evidenceSet];

    // Top 3 risks (lowest scoring, highest weighted controls in this domain)
    const top_risks = [...sectionPriorityGaps]
      .sort((a, b) => {
        if ((a.score ?? 2) !== (b.score ?? 2)) return (a.score ?? 2) - (b.score ?? 2);
        return (b.weight || 0) - (a.weight || 0);
      })
      .slice(0, 3)
      .map(g => g.gap?.replace(/\?$/, '') || g.field_id);

    // Priority Controls to Address — top 3 by weight + low score
    const priority_controls = [...sectionPriorityGaps]
      .sort((a, b) => {
        const aHardness = (a.score === 0 ? 0 : a.score === 1 ? 1 : 2);
        const bHardness = (b.score === 0 ? 0 : b.score === 1 ? 1 : 2);
        if (aHardness !== bHardness) return aHardness - bHardness;
        return (b.weight || 0) - (a.weight || 0);
      })
      .slice(0, 3)
      .map(g => ({
        control: g.gap?.replace(/\?$/, '') || g.field_id,
        weight: g.weight || 0,
        status: g.score === 0 ? 'Fail' : g.score === 1 ? 'Partial' : 'Pass',
      }));

    // Consultant Insight — interpret pattern, do not restate data
    let consultant_insight;
    const partialCount = sectionPriorityGaps.filter(g => g.score === 1).length;
    const failCount = sectionPriorityGaps.filter(g => g.score === 0).length;
    if (failCount === 0 && partialCount > 0) {
      consultant_insight = `The primary issue in this domain is not the absence of controls, but inconsistent enforcement and lack of audit-ready evidence, which will reduce assessor confidence and prolong CSQ validation.`;
    } else if (failCount > 0 && partialCount > 0) {
      consultant_insight = `${SUPER_DOMAIN_LABELS[superId]} shows a mix of unimplemented controls and partial coverage. Closing the failed controls is necessary but not sufficient; the partial controls must be enforced and evidenced before submission becomes defensible.`;
    } else if (failCount > 0) {
      consultant_insight = `Foundational controls in this domain are missing rather than weak. Remediation is structural and should be sequenced ahead of policy and evidence work.`;
    } else if (pct >= 75) {
      consultant_insight = `${SUPER_DOMAIN_LABELS[superId]} is operationally mature; remaining work is documentation, evidence centralisation, and review cadence rather than control implementation.`;
    } else {
      consultant_insight = `Controls in this domain are uneven across the assessed environment; the organisation must drive consistency through governance and centralised tooling rather than point fixes.`;
    }

    result[superId] = {
      domain_label: SUPER_DOMAIN_LABELS[superId],
      score_percent: pct,
      gating_failures: sectionGatingFails,
      risk_to_submission: {
        will_fail_csq,
        defence_will_clarify,
        is_gating_failure,
        narrative: risk_narrative,
      },
      top_risks,
      priority_controls,
      consultant_insight,
      control_priority_table,
      evidence_required,
    };
  });

  return result;
};

// ============================================================================
// SECTION 5 — GATING CONTROL ANALYSIS
// ============================================================================
const buildGatingAnalysis = (ctx) => {
  const { gatingStatus } = ctx;
  const controls = gatingStatus?.controls || [];

  const gating_table = controls.map(c => {
    let status, impact;
    if (c.score === 2) { status = 'Pass'; impact = 'Compliant - no submission impact'; }
    else if (c.score === 1) { status = 'Partial'; impact = 'High audit risk - clarification likely'; }
    else if (c.score === 0) { status = 'Fail'; impact = 'Blocks submission'; }
    else { status = 'Unanswered'; impact = 'Moderate risk - requires assessment'; }

    return {
      control: c.prompt?.replace(/\?$/, '') || c.field_id,
      field_id: c.field_id,
      section_id: c.section_id,
      status,
      impact,
    };
  });

  const failCount = gating_table.filter(g => g.status === 'Fail').length;
  const partialCount = gating_table.filter(g => g.status === 'Partial').length;
  const passCount = gating_table.filter(g => g.status === 'Pass').length;

  let insight;
  if (failCount > 0 && partialCount > 0) {
    insight = `${failCount} gating control${failCount > 1 ? 's are' : ' is'} fully failed and ${partialCount} ${partialCount > 1 ? 'are' : 'is'} only partially implemented. The fully-failed controls are absolute submission blockers - Defence will reject the CSQ until each is implemented to a demonstrable standard. The partial controls lack consistent enforcement and audit-ready evidence and must be uplifted before a defensible submission is possible.`;
  } else if (failCount > 0) {
    insight = `${failCount} gating control${failCount > 1 ? 's are' : ' is'} fully failed. These represent absolute submission blockers - Defence will reject the CSQ until each is implemented to a demonstrable standard. Remediation of these failures must precede all other uplift activity.`;
  } else if (partialCount > 0) {
    insight = `While no gating controls are fully failed, ${partialCount} gating control${partialCount > 1 ? 's are' : ' is'} only partially implemented and lack${partialCount > 1 ? '' : 's'} consistent enforcement and audit-ready evidence. This is sufficient to prevent a defensible DISP submission and Defence will request clarification on each control during validation.`;
  } else {
    insight = `All ${passCount} gating controls are fully implemented. The organisation has cleared the structural prerequisites for DISP submission, subject to evidence completeness across the broader control set.`;
  }

  return {
    gating_table,
    summary: { fail: failCount, partial: partialCount, pass: passCount, total: gating_table.length },
    insight,
  };
};

// ============================================================================
// SECTION 6 — EVIDENCE READINESS & DEFENSIBILITY
// ============================================================================
const buildEvidenceReadiness = (ctx) => {
  const { evidenceMultiplier, policyEnforcementMultiplier, csqMapping, gatingStatus } = ctx;

  let evidence_maturity_score;
  if (evidenceMultiplier >= 0.95) evidence_maturity_score = 'High';
  else if (evidenceMultiplier >= 0.85) evidence_maturity_score = 'Medium';
  else evidence_maturity_score = 'Low';

  // Gating-aware downgrade: maturity rating cannot read 'High' or 'Medium' alongside
  // findings that show controls cannot be evidenced. Keep wording internally consistent.
  const gatingFails = gatingStatus?.score0Count || 0;
  const csqUnsupportable = (csqMapping?.summary?.no || 0) >= 3;
  if (evidence_maturity_score === 'High' && (gatingFails > 0 || csqUnsupportable)) {
    evidence_maturity_score = 'Medium';
  }
  if (evidence_maturity_score === 'Medium' && gatingFails >= 3 && csqUnsupportable) {
    evidence_maturity_score = 'Low';
  }

  const findings = [];
  if (evidenceMultiplier < 0.85) findings.push('Evidence is not centrally collected or readily defensible to a Defence assessor.');
  if (evidenceMultiplier < 0.70) findings.push('Critical gaps in audit trail - control effectiveness cannot be demonstrated through artefacts.');
  if (policyEnforcementMultiplier < 0.85) findings.push('Policy enforcement is inconsistent; documented intent is not matched by technical or procedural evidence.');
  if (gatingFails > 0) findings.push(`${gatingFails} gating control${gatingFails > 1 ? 's lack' : ' lacks'} the implementation evidence required for CSQ validation.`);
  if (csqMapping?.summary?.no >= 3) findings.push('Multiple CSQ categories cannot be evidenced - submission would be unsupportable in current form.');
  if (findings.length === 0) findings.push('Evidence posture is broadly defensible; remaining work is centralisation and audit traceability refinement.');

  const required_improvements = [
    'Establish a centralised evidence register mapping each control to its source artefacts',
    'Map each piece of evidence to the corresponding DISP/CSQ requirement and Essential Eight control',
    'Apply version control and dated reviews to all policy and procedure documents',
    'Build audit traceability from policy -> control configuration -> operational log -> review record',
    'Standardise evidence formats (configuration exports, log samples, review records) for assessor consumption',
  ];

  const defence_lens = 'Defence evaluates evidence, not intent. Controls must be demonstrable, repeatable, and traceable through artefacts that an external assessor can inspect without relying on staff narrative.';

  return {
    evidence_maturity_score,
    evidence_multiplier: evidenceMultiplier,
    policy_enforcement_multiplier: policyEnforcementMultiplier,
    findings,
    required_improvements,
    defence_lens,
  };
};

// ============================================================================
// SECTION 7 — CSQ ENHANCEMENTS (per-block submission risk + numbered path)
// ============================================================================
const buildCsqEnhancements = (ctx) => {
  const { csqMapping } = ctx;
  if (!csqMapping?.domains) return [];

  const enriched = [];
  csqMapping.domains.forEach(domain => {
    domain.blocks.forEach(block => {
      let submission_risk;
      if (block.has_gating && block.compliance_status === 'non_compliant') submission_risk = 'High';
      else if (block.compliance_status === 'non_compliant') submission_risk = 'High';
      else if (block.compliance_status === 'partially_compliant') submission_risk = 'Medium';
      else submission_risk = 'Low';

      const numbered_minimum_path = (block.minimum_viable_path || []).slice(0, 5).map((step, idx) => ({
        step: idx + 1,
        action: step,
      }));

      enriched.push({
        csq_domain: domain.domain_label,
        csq_category: block.csq_category,
        combined_name: block.combined_name,
        compliance_status: block.compliance_status,
        response_direction: block.response_direction,
        has_gating: block.has_gating,
        submission_risk,
        numbered_minimum_path,
      });
    });
  });

  return enriched;
};

// ============================================================================
// SECTION 8 — PRIORITISED REMEDIATION ROADMAP
// ============================================================================
const buildRemediationRoadmap = (ctx) => {
  const { priorityGaps, gatingStatus } = ctx;
  const gatingFieldIds = new Set((gatingStatus?.controls || [])
    .filter(c => c.score < 2)
    .map(c => c.field_id));

  const toAction = (g) => ({
    action: g.gap?.replace(/^Is\s+/i, 'Address: ').replace(/\?$/, '') || 'Remediate identified control gap',
    control_gap: g.field_id,
    domain: g.domain,
    priority: g.priority,
    is_gating: gatingFieldIds.has(g.field_id),
  });

  const phase1 = priorityGaps
    .filter(g => gatingFieldIds.has(g.field_id) || g.priority === 'critical')
    .map(toAction);
  const phase1Ids = new Set(phase1.map(a => a.control_gap));
  const phase2 = priorityGaps
    .filter(g => g.priority === 'high' && !phase1Ids.has(g.field_id))
    .map(toAction);
  const phase2Ids = new Set(phase2.map(a => a.control_gap));
  const phase3 = priorityGaps
    .filter(g => (g.priority === 'medium' || g.priority === 'low') && !phase1Ids.has(g.field_id) && !phase2Ids.has(g.field_id))
    .map(toAction);

  return {
    phase_1: {
      timeline: 'Weeks 1-3',
      focus: 'Critical and gating control remediation',
      actions: phase1,
    },
    phase_2: {
      timeline: 'Weeks 4-6',
      focus: 'High-priority controls and policy uplift',
      actions: phase2,
    },
    phase_3: {
      timeline: 'Weeks 7-10',
      focus: 'Optimisation, evidence centralisation, and audit readiness',
      actions: phase3,
    },
  };
};

// ============================================================================
// SECTION 9 — FINAL RECOMMENDATION (3-part: current state / risk / next step)
// ============================================================================
const buildFinalRecommendation = (ctx, decisionView, executiveSnapshot, evidenceReadiness) => {
  const { engagement, finalScore, gatingStatus, priorityGaps } = ctx;
  const decision = decisionView.final_recommendation;
  const client = engagement?.client_name || 'The organisation';
  const gatingFails = gatingStatus?.score0Count || 0;
  const gatingPartials = gatingStatus?.score1Count || 0;
  const criticalGaps = priorityGaps.filter(g => g.priority === 'critical').length;

  // 1) Current State — data-driven
  const evidenceLevel = evidenceReadiness?.evidence_maturity_score || 'Low';
  let current_state = `${client} is operating at a weighted ML2 score of ${finalScore}% (${executiveSnapshot.score_band}) with ${evidenceLevel.toLowerCase()} evidence maturity. `;
  if (gatingFails > 0 && gatingPartials > 0) {
    current_state += `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed and ${gatingPartials} ${gatingPartials > 1 ? 'are' : 'is'} partially implemented; ${criticalGaps} critical control gap${criticalGaps !== 1 ? 's exist' : ' exists'} across the broader assessment.`;
  } else if (gatingFails > 0) {
    current_state += `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed; ${criticalGaps} critical control gap${criticalGaps !== 1 ? 's exist' : ' exists'} across the broader assessment.`;
  } else if (gatingPartials > 0) {
    current_state += `All gating controls are at least partially implemented but ${gatingPartials} lack${gatingPartials > 1 ? '' : 's'} consistent enforcement and audit-ready evidence; ${criticalGaps} critical control gap${criticalGaps !== 1 ? 's exist' : ' exists'} across the broader assessment.`;
  } else {
    current_state += `All gating controls are fully implemented; ${criticalGaps} critical control gap${criticalGaps !== 1 ? 's exist' : ' exists'} across the broader assessment.`;
  }

  // 2) Risk of proceeding now — direct, advisory
  let risk_of_proceeding;
  if (decision === 'Do Not Submit') {
    risk_of_proceeding = 'Proceeding with submission in the current state will likely result in CSQ rejection or substantial clarification requests, exposing the organisation to commercial delay and reputational risk with Defence.';
  } else if (decision === 'Submit with Conditions') {
    risk_of_proceeding = 'Proceeding without prior remediation will likely result in Defence clarification cycles and supplementary evidence requests, extending validation timelines and creating uncertainty for any contract conversations dependent on active DISP membership.';
  } else {
    risk_of_proceeding = 'Submission risk is low. Residual risk is limited to evidence currency and will be managed through standard review cadence rather than remediation.';
  }

  // 3) Next step — clear, prescriptive
  let next_step;
  if (decision === 'Do Not Submit') {
    next_step = `The organisation must complete Phase 1 of the remediation roadmap (gating and critical controls) before any CSQ submission is attempted. Estimated time to a defensible position: ${executiveSnapshot.estimated_time_to_readiness}.`;
  } else if (decision === 'Submit with Conditions') {
    next_step = `The organisation must close the partial gating controls and centralise evidence in parallel with submission preparation. The submission package must explicitly acknowledge remediation in flight and reference a credible delivery plan.`;
  } else {
    next_step = `The organisation must package existing evidence into a structured submission pack, validate the currency of all policy artefacts, and coordinate the formal CSQ submission with the assigned Security Officer.`;
  }

  // Backwards-compatible single-string statement for any consumer that still reads it
  const statement = `${current_state} ${risk_of_proceeding} ${next_step}`;

  return {
    decision,
    reason: decisionView.final_recommendation_reason,
    current_state,
    risk_of_proceeding,
    next_step,
    statement,
  };
};

// ============================================================================
// SECTION 10 — TECHNOLOGY -> CONTROL COVERAGE MAPPING
// ============================================================================
// Static, audit-defensible mapping of major platforms to the DISP/Essential Eight
// controls they address. Used to make the gap-to-tooling link explicit.
const COMPONENT_CONTROL_MAP = {
  microsoft_entra_id: { label: 'Microsoft Entra ID', controls: ['MFA enforcement', 'Conditional access policies', 'Identity governance', 'Privileged identity management'] },
  conditional_access: { label: 'Conditional Access', controls: ['Risk-based access policies', 'Device compliance gating', 'Session control'] },
  microsoft_intune: { label: 'Microsoft Intune', controls: ['Device compliance enforcement', 'Endpoint security baselines', 'BYOD governance', 'Remote wipe'] },
  microsoft_defender_for_endpoint: { label: 'Microsoft Defender for Endpoint', controls: ['Endpoint Detection and Response', 'Application execution monitoring', 'Threat intelligence'] },
  wdac_or_applocker: { label: 'WDAC / AppLocker', controls: ['Application allow-listing', 'Restriction of unapproved software'] },
  microsoft_sentinel: { label: 'Microsoft Sentinel', controls: ['Centralised SIEM', 'Security alert monitoring', 'Incident detection and response'] },
  privileged_access_management: { label: 'Privileged Access Management', controls: ['Just-in-time admin access', 'Privileged session monitoring', 'Admin credential vaulting'] },
  backup_platform: { label: 'Backup Platform', controls: ['Daily backup execution', 'Immutable backup copies', 'Restore testing'] },
  tenable: { label: 'Tenable', controls: ['Vulnerability scanning', 'Patch compliance reporting'] },
  qualys: { label: 'Qualys', controls: ['Vulnerability scanning', 'Patch compliance reporting'] },
  drata: { label: 'Drata', controls: ['Compliance automation', 'Continuous evidence collection', 'Control monitoring'] },
  remediation_tracker: { label: 'Remediation Tracker', controls: ['Vulnerability remediation tracking', 'SLA enforcement'] },
  evidence_pack_generator: { label: 'Evidence Pack Generator', controls: ['Audit-ready evidence packaging', 'CSQ submission support'] },
  disp_dashboard: { label: 'DISP Dashboard', controls: ['DISP CSQ tracking', 'Membership status visibility'] },
};

const buildTechnologyControlCoverage = (ctx) => {
  const { domainScores, gatingStatus } = ctx;
  // Identify ICT super-domain weak/missing posture
  const ictSections = SUPER_DOMAIN_SECTIONS.ict_security;
  const ictAvg = weightedAvg(ictSections, domainScores);
  const ictGatingFails = (gatingStatus?.controls || []).filter(c => c.score === 0 && ictSections.includes(c.section_id)).length;
  const ictGatingPartials = (gatingStatus?.controls || []).filter(c => c.score === 1 && ictSections.includes(c.section_id)).length;

  const platforms = Object.values(COMPONENT_CONTROL_MAP);

  // Insight links missing tooling to identified gaps
  let insight;
  if (ictGatingFails > 0 || ictGatingPartials > 0) {
    insight = `The absence or inconsistent rollout of these platforms directly correlates to the gaps identified in Identity, Endpoint Management, and Logging & Monitoring. Closing the ICT gating controls without an integrated platform footprint will result in fragile, manually-evidenced controls that will not survive Defence audit scrutiny.`;
  } else if (ictAvg < 75) {
    insight = `The platforms in scope are present but unevenly enforced. The organisation must standardise configuration baselines and surface enforcement evidence centrally; piecemeal tooling will not produce a defensible CSQ submission.`;
  } else {
    insight = `The platforms in scope are present and operating at a maturity level consistent with DISP expectations. Remaining work is evidence centralisation rather than tooling acquisition.`;
  }

  return {
    platforms,
    insight,
    ict_score_percent: ictAvg,
  };
};

// ============================================================================
// PUBLIC API
// ============================================================================
export const buildHybridReport = (ctx) => {
  const executive_snapshot = buildExecutiveSnapshot(ctx);
  const commercial_impact = buildCommercialImpact(ctx, executive_snapshot);
  const how_to_read = buildHowToRead();
  const decision_view = buildDecisionView(ctx);
  const domain_enhancements = buildDomainEnhancements(ctx);
  const gating_analysis = buildGatingAnalysis(ctx);
  const evidence_readiness = buildEvidenceReadiness(ctx);
  const csq_enhancements = buildCsqEnhancements(ctx);
  const remediation_roadmap = buildRemediationRoadmap(ctx);
  const technology_control_coverage = buildTechnologyControlCoverage(ctx);
  const final_recommendation = buildFinalRecommendation(ctx, decision_view, executive_snapshot, evidence_readiness);

  return {
    schema_version: '1.1.0',
    generated_at: new Date().toISOString(),
    executive_snapshot,
    commercial_impact,
    how_to_read,
    decision_view,
    domain_enhancements,
    gating_analysis,
    evidence_readiness,
    csq_enhancements,
    remediation_roadmap,
    technology_control_coverage,
    final_recommendation,
  };
};

export default buildHybridReport;
